import { useRef, useState, useMemo, useCallback } from "react";

import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import {
    BoxMinimalistic,
    DocumentAdd,
    DocumentText,
} from "@solar-icons/react/ssr";
import { useDispatch } from "react-redux";
import { Plus } from "@/assets/svgs/Plus";

import {
    PERMISSION_POST_PROJECTS,
    PERMISSION_POST_BIDS,
} from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { FEATURE_BIDS } from "@/constants";
import { NavItemIcon } from "@/nav/NavIcons";
import { openProjectPopup, openBidPopup } from "@/nav/navSlice";

const createMenuItems = [
    {
        key: "create-project",
        label: "Project",
        icon: BoxMinimalistic,
        permission: PERMISSION_POST_PROJECTS,
        testId: "create-project-menu-item",
    },
    {
        key: "create-bid",
        label: "Bid Sheet",
        icon: DocumentText,
        feature: FEATURE_BIDS,
        permission: PERMISSION_POST_BIDS,
        testId: "create-bid-menu-item",
    },
    {
        key: "create-change-order",
        label: "Change Order",
        icon: DocumentAdd,
        feature: FEATURE_BIDS,
        permission: PERMISSION_POST_BIDS,
        testId: "create-change-order-menu-item",
    },
];

export default function CreateButton() {
    const buttonRef = useRef();
    const dispatch = useDispatch();
    const { canAccess, hasPermission } = useEntitlements();

    const [open, setOpen] = useState(false);
    const buttonId = "create-button-trigger";
    const menuId = "create-menu";

    const handleProjectPopupOpen = useCallback(() => {
        dispatch(openProjectPopup());
        setOpen(false);
    }, [dispatch]);

    const handleBidPopupOpen = useCallback(() => {
        dispatch(
            openBidPopup({
                type: "Bid Sheet",
            }),
        );
        setOpen(false);
    }, [dispatch]);

    const handleChangeOrderPopupOpen = useCallback(() => {
        dispatch(
            openBidPopup({
                type: "Change Order",
            }),
        );
        setOpen(false);
    }, [dispatch]);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const menuItems = useMemo(() => {
        const handleSelectByKey = {
            "create-project": handleProjectPopupOpen,
            "create-bid": handleBidPopupOpen,
            "create-change-order": handleChangeOrderPopupOpen,
        };

        return createMenuItems
            .filter((item) =>
                item.feature
                    ? canAccess(item.feature, item.permission)
                    : hasPermission(item.permission),
            )
            .map((item) => (
                <CreateMenuItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    testId={item.testId}
                    onClick={handleSelectByKey[item.key]}
                />
            ));
    }, [
        hasPermission,
        canAccess,
        handleProjectPopupOpen,
        handleBidPopupOpen,
        handleChangeOrderPopupOpen,
    ]);

    if (
        !hasPermission(PERMISSION_POST_PROJECTS) &&
        !canAccess(FEATURE_BIDS, PERMISSION_POST_BIDS)
    ) {
        return null;
    }

    return (
        <>
            <div ref={buttonRef}>
                <Button
                    variant="contained"
                    color="secondary"
                    id={buttonId}
                    onClick={handleOpen}
                    data-testid="create-button"
                    aria-controls={open ? menuId : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="menu"
                    endIcon={<Plus />}
                    disableRipple
                    sx={(theme) => ({
                        width: 78,
                        minWidth: 78,
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        gap: "5px",
                        transition: theme.transitions.create(
                            ["background-color", "transform"],
                            {
                                easing: theme.transitions.easing.easeOut,
                                duration: 300,
                            },
                        ),
                        "& .MuiButton-endIcon": {
                            margin: 0,
                        },
                        "&:hover": {
                            backgroundColor: theme.palette.secondary.light,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                    })}
                >
                    {"Create"}
                </Button>
            </div>
            <Menu
                id={menuId}
                onClose={() => {
                    setOpen(false);
                }}
                open={open}
                anchorEl={() => buttonRef.current}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                BackdropProps={{
                    invisible: true,
                    sx: {
                        backdropFilter: "none",
                        backgroundColor: "transparent",
                    },
                }}
                PaperProps={{
                    sx: (theme) => ({
                        width: 139,
                        marginTop: "14px",
                        borderRadius: "14px",
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: "none",
                        boxShadow: "0px 0px 50px rgba(0, 0, 0, 0.14)",
                        "& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-focusVisible":
                            {
                                backgroundColor: "transparent",
                            },
                    }),
                }}
                MenuListProps={{
                    "aria-labelledby": buttonId,
                    sx: {
                        padding: "10px 0",
                    },
                }}
            >
                {menuItems.length > 0 ? (
                    menuItems
                ) : (
                    <CreateMenuItem disabled label="No options available" />
                )}
            </Menu>
        </>
    );
}

function CreateMenuItem({ disabled = false, icon, label, onClick, testId }) {
    return (
        <MenuItem
            disableRipple
            disabled={disabled}
            onClick={onClick}
            data-testid={testId}
            sx={(theme) => ({
                minHeight: 29,
                padding: "6px 14px",
                color: theme.palette.text.primary,
                gap: "7px",
            })}
        >
            {icon && (
                <NavItemIcon
                    icon={icon}
                    size={13}
                    sx={{ borderRadius: "5px" }}
                />
            )}
            <Typography
                component="span"
                sx={(theme) => ({
                    color: "inherit",
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                })}
            >
                {label}
            </Typography>
        </MenuItem>
    );
}
